import React, { useState, useCallback } from 'react';
import './AdvancedSearch.css';

// MTG colors for selection (W, U, B, R, G, C)
// Phyrexian is NOT a color - it's a mana type modifier that's handled automatically
const COLORS = [
  { code: 'W', name: 'White', svgFile: 'W' },
  { code: 'U', name: 'Blue', svgFile: 'U' },
  { code: 'B', name: 'Black', svgFile: 'B' },
  { code: 'R', name: 'Red', svgFile: 'R' },
  { code: 'G', name: 'Green', svgFile: 'G' },
  { code: 'C', name: 'Colorless', svgFile: 'C' },
];

const RARITIES = ['common', 'uncommon', 'rare', 'mythic'];

const FORMATS = [
  'standard', 'pioneer', 'modern', 'legacy', 'vintage', 
  'commander', 'pauper', 'historic', 'brawl'
];

const LANGUAGES = [
  { code: '', name: 'Any Language' },
  { code: 'en', name: 'English' },
  { code: 'ja', name: 'Japanese (日本語)' },
  { code: 'es', name: 'Spanish (Español)' },
  { code: 'fr', name: 'French (Français)' },
  { code: 'de', name: 'German (Deutsch)' },
  { code: 'it', name: 'Italian (Italiano)' },
  { code: 'pt', name: 'Portuguese (Português)' },
  { code: 'ko', name: 'Korean (한국어)' },
  { code: 'ru', name: 'Russian (Русский)' },
  { code: 'zhs', name: 'Simplified Chinese (简体中文)' },
  { code: 'zht', name: 'Traditional Chinese (繁體中文)' },
];

const COMPARISON_OPS = [
  { value: 'eq', label: '=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
];

/**
 * Color Symbol Component - renders SVG mana symbol for color picker
 */
function ColorSymbol({ svgFile, name, apiUrl }) {
  const svgUrl = `${apiUrl}/images/mtg/base-assets/Resource-symbols/${svgFile}.svg`;
  
  return (
    <img 
      src={svgUrl} 
      alt={name}
      className="color-symbol-img"
      onError={(e) => {
        // Fallback to text if SVG not found
        e.target.style.display = 'none';
        if (e.target.nextSibling) {
          e.target.nextSibling.style.display = 'inline';
        }
      }}
    />
  );
}

function AdvancedSearch({ onSearch, apiUrl }) {
  const [criteria, setCriteria] = useState({
    name: { value: '', operator: 'contains' },
    type_line: { values: [''], operator: 'OR' },
    colors: { values: [], operator: 'exact', hybridMode: 'explicit' },
    color_identity: { values: [], operator: 'exact' },
    cmc: { value: '', operator: 'eq' },
    power: { value: '', operator: 'eq' },
    toughness: { value: '', operator: 'eq' },
    oracle_text: { value: '' },
    rarity: { values: [] },
    set: { value: '' },
    artist: { values: [''], operator: 'OR' },
    legality: { format: '', status: 'legal' },
    keywords: { values: [''], operator: 'OR' },
    lang: { value: '' },
  });

  const [expanded, setExpanded] = useState({
    basic: true,
    colors: false,
    stats: false,
    other: false,
  });

  const updateCriteria = useCallback((field, updates) => {
    setCriteria(prev => ({
      ...prev,
      [field]: { ...prev[field], ...updates }
    }));
  }, []);

  const handleColorToggle = useCallback((field, colorCode) => {
    setCriteria(prev => {
      const current = prev[field].values;
      const newValues = current.includes(colorCode)
        ? current.filter(c => c !== colorCode)
        : [...current, colorCode];
      return {
        ...prev,
        [field]: { ...prev[field], values: newValues }
      };
    });
  }, []);

  const handleRarityToggle = useCallback((rarity) => {
    setCriteria(prev => {
      const current = prev.rarity.values;
      const newValues = current.includes(rarity)
        ? current.filter(r => r !== rarity)
        : [...current, rarity];
      return {
        ...prev,
        rarity: { values: newValues }
      };
    });
  }, []);

  const addArrayField = useCallback((field) => {
    setCriteria(prev => ({
      ...prev,
      [field]: { ...prev[field], values: [...prev[field].values, ''] }
    }));
  }, []);

  const updateArrayField = useCallback((field, index, value) => {
    setCriteria(prev => {
      const newValues = [...prev[field].values];
      newValues[index] = value;
      return {
        ...prev,
        [field]: { ...prev[field], values: newValues }
      };
    });
  }, []);

  const removeArrayField = useCallback((field, index) => {
    setCriteria(prev => {
      const newValues = prev[field].values.filter((_, i) => i !== index);
      return {
        ...prev,
        [field]: { ...prev[field], values: newValues.length ? newValues : [''] }
      };
    });
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    
    // Build clean criteria object, removing empty values
    const cleanCriteria = {};
    
    if (criteria.name.value.trim()) {
      cleanCriteria.name = criteria.name;
    }
    
    const typeValues = criteria.type_line.values.filter(v => v.trim());
    if (typeValues.length) {
      cleanCriteria.type_line = { ...criteria.type_line, values: typeValues };
    }
    
    // Handle colors - send directly to backend
    if (criteria.colors.values.length) {
      cleanCriteria.colors = {
        values: criteria.colors.values,
        operator: criteria.colors.operator,
        hybridMode: criteria.colors.hybridMode
      };
    }
    
    // Handle color identity
    if (criteria.color_identity.values.length) {
      cleanCriteria.color_identity = {
        values: criteria.color_identity.values,
        operator: criteria.color_identity.operator
      };
    }
    
    if (criteria.cmc.value !== '') {
      cleanCriteria.cmc = criteria.cmc;
    }
    
    if (criteria.power.value !== '') {
      cleanCriteria.power = criteria.power;
    }
    
    if (criteria.toughness.value !== '') {
      cleanCriteria.toughness = criteria.toughness;
    }
    
    if (criteria.oracle_text.value.trim()) {
      cleanCriteria.oracle_text = criteria.oracle_text;
    }
    
    if (criteria.rarity.values.length) {
      cleanCriteria.rarity = criteria.rarity;
    }
    
    if (criteria.set.value.trim()) {
      cleanCriteria.set = criteria.set;
    }
    
    const artistValues = criteria.artist.values.filter(v => v.trim());
    if (artistValues.length) {
      cleanCriteria.artist = { ...criteria.artist, values: artistValues };
    }
    
    if (criteria.legality.format) {
      cleanCriteria.legality = criteria.legality;
    }
    
    const keywordValues = criteria.keywords.values.filter(v => v.trim());
    if (keywordValues.length) {
      cleanCriteria.keywords = { ...criteria.keywords, values: keywordValues };
    }
    
    if (criteria.lang.value) {
      cleanCriteria.lang = criteria.lang;
    }
    
    if (Object.keys(cleanCriteria).length === 0) {
      return;
    }
    
    onSearch(cleanCriteria);
  }, [criteria, onSearch]);

  const handleReset = useCallback(() => {
    setCriteria({
      name: { value: '', operator: 'contains' },
      type_line: { values: [''], operator: 'OR' },
      colors: { values: [], operator: 'exact', hybridMode: 'explicit' },
      color_identity: { values: [], operator: 'exact' },
      cmc: { value: '', operator: 'eq' },
      power: { value: '', operator: 'eq' },
      toughness: { value: '', operator: 'eq' },
      oracle_text: { value: '' },
      rarity: { values: [] },
      set: { value: '' },
      artist: { values: [''], operator: 'OR' },
      legality: { format: '', status: 'legal' },
      keywords: { values: [''], operator: 'OR' },
      lang: { value: '' },
    });
  }, []);

  const toggleSection = useCallback((section) => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  return (
    <form className="advanced-search" onSubmit={handleSubmit}>
      {/* Basic Section */}
      <div className="search-section">
        <button
          type="button"
          className={`section-header ${expanded.basic ? 'expanded' : ''}`}
          onClick={() => toggleSection('basic')}
        >
          <span className="section-icon">{expanded.basic ? '▼' : '▶'}</span>
          <span className="section-title">Basic Search</span>
        </button>
        
        {expanded.basic && (
          <div className="section-content">
            {/* Card Name */}
            <div className="field-group">
              <label className="field-label">Card Name</label>
              <div className="field-row">
                <input
                  type="text"
                  className="field-input"
                  placeholder="e.g., Jhoira"
                  value={criteria.name.value}
                  onChange={(e) => updateCriteria('name', { value: e.target.value })}
                />
                <select
                  className="field-select small"
                  value={criteria.name.operator}
                  onChange={(e) => updateCriteria('name', { operator: e.target.value })}
                >
                  <option value="contains">Contains</option>
                  <option value="exact">Exact</option>
                </select>
              </div>
            </div>

            {/* Type Line */}
            <div className="field-group">
              <label className="field-label">
                Type Line
                <select
                  className="operator-select"
                  value={criteria.type_line.operator}
                  onChange={(e) => updateCriteria('type_line', { operator: e.target.value })}
                >
                  <option value="OR">Match Any (OR)</option>
                  <option value="AND">Match All (AND)</option>
                </select>
              </label>
              {criteria.type_line.values.map((value, index) => (
                <div key={index} className="field-row">
                  <input
                    type="text"
                    className="field-input"
                    placeholder="e.g., Goblin, Creature, Legendary"
                    value={value}
                    onChange={(e) => updateArrayField('type_line', index, e.target.value)}
                  />
                  {criteria.type_line.values.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeArrayField('type_line', index)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="add-btn"
                onClick={() => addArrayField('type_line')}
              >
                + Add Type
              </button>
            </div>

            {/* Oracle Text */}
            <div className="field-group">
              <label className="field-label">Card Text</label>
              <input
                type="text"
                className="field-input"
                placeholder="e.g., draw a card, flying"
                value={criteria.oracle_text.value}
                onChange={(e) => updateCriteria('oracle_text', { value: e.target.value })}
              />
            </div>
          </div>
        )}
      </div>

      {/* Colors Section */}
      <div className="search-section">
        <button
          type="button"
          className={`section-header ${expanded.colors ? 'expanded' : ''}`}
          onClick={() => toggleSection('colors')}
        >
          <span className="section-icon">{expanded.colors ? '▼' : '▶'}</span>
          <span className="section-title">Colors</span>
        </button>
        
        {expanded.colors && (
          <div className="section-content">
            {/* Card Colors */}
            <div className="field-group">
              <label className="field-label">
                Card Colors
                <select
                  className="operator-select"
                  value={criteria.colors.operator}
                  onChange={(e) => updateCriteria('colors', { operator: e.target.value })}
                >
                  <option value="exact">Exactly these colors</option>
                  <option value="include">Including these colors</option>
                  <option value="atMost">At most these colors</option>
                </select>
              </label>
              <div className="color-selector">
                {COLORS.map(color => (
                  <button
                    key={color.code}
                    type="button"
                    className={`color-btn color-${color.code.toLowerCase()} ${
                      criteria.colors.values.includes(color.code) ? 'selected' : ''
                    }`}
                    onClick={() => handleColorToggle('colors', color.code)}
                    title={color.name}
                  >
                    <ColorSymbol svgFile={color.svgFile} name={color.name} apiUrl={apiUrl} />
                    <span className="color-symbol-fallback" style={{ display: 'none' }}>{color.code}</span>
                  </button>
                ))}
              </div>
              
              {/* Hybrid Mana Matching */}
              <div className="hybrid-mana-options">
                <span className="hybrid-label">Hybrid Mana:</span>
                <div className="radio-group">
                  <label className="radio-label" title="Hybrid symbol {U/R} counts as BOTH Blue AND Red">
                    <input
                      type="radio"
                      name="hybridMode"
                      value="explicit"
                      checked={criteria.colors.hybridMode === 'explicit'}
                      onChange={(e) => updateCriteria('colors', { hybridMode: e.target.value })}
                    />
                    Explicit (both colors)
                  </label>
                  <label className="radio-label" title="Hybrid symbol {U/R} counts as Blue OR Red">
                    <input
                      type="radio"
                      name="hybridMode"
                      value="implicit"
                      checked={criteria.colors.hybridMode === 'implicit'}
                      onChange={(e) => updateCriteria('colors', { hybridMode: e.target.value })}
                    />
                    Implicit (either color)
                  </label>
                </div>
              </div>
            </div>

            {/* Color Identity */}
            <div className="field-group">
              <label className="field-label">
                Color Identity (Commander)
                <select
                  className="operator-select"
                  value={criteria.color_identity.operator}
                  onChange={(e) => updateCriteria('color_identity', { operator: e.target.value })}
                >
                  <option value="exact">Exactly these colors</option>
                  <option value="include">Including these colors</option>
                  <option value="atMost">At most these colors</option>
                </select>
              </label>
              <div className="color-selector">
                {COLORS.map(color => (
                  <button
                    key={color.code}
                    type="button"
                    className={`color-btn color-${color.code.toLowerCase()} ${
                      criteria.color_identity.values.includes(color.code) ? 'selected' : ''
                    }`}
                    onClick={() => handleColorToggle('color_identity', color.code)}
                    title={color.name}
                  >
                    <ColorSymbol svgFile={color.svgFile} name={color.name} apiUrl={apiUrl} />
                    <span className="color-symbol-fallback" style={{ display: 'none' }}>{color.code}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Section */}
      <div className="search-section">
        <button
          type="button"
          className={`section-header ${expanded.stats ? 'expanded' : ''}`}
          onClick={() => toggleSection('stats')}
        >
          <span className="section-icon">{expanded.stats ? '▼' : '▶'}</span>
          <span className="section-title">Mana & Stats</span>
        </button>
        
        {expanded.stats && (
          <div className="section-content">
            {/* CMC */}
            <div className="field-group">
              <label className="field-label">Mana Value (CMC)</label>
              <div className="field-row">
                <select
                  className="field-select small"
                  value={criteria.cmc.operator}
                  onChange={(e) => updateCriteria('cmc', { operator: e.target.value })}
                >
                  {COMPARISON_OPS.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  className="field-input small"
                  min="0"
                  step="1"
                  placeholder="0"
                  value={criteria.cmc.value}
                  onChange={(e) => updateCriteria('cmc', { value: e.target.value })}
                />
              </div>
            </div>

            {/* Power */}
            <div className="field-group">
              <label className="field-label">Power</label>
              <div className="field-row">
                <select
                  className="field-select small"
                  value={criteria.power.operator}
                  onChange={(e) => updateCriteria('power', { operator: e.target.value })}
                >
                  {COMPARISON_OPS.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  className="field-input small"
                  min="0"
                  placeholder="0"
                  value={criteria.power.value}
                  onChange={(e) => updateCriteria('power', { value: e.target.value })}
                />
              </div>
            </div>

            {/* Toughness */}
            <div className="field-group">
              <label className="field-label">Toughness</label>
              <div className="field-row">
                <select
                  className="field-select small"
                  value={criteria.toughness.operator}
                  onChange={(e) => updateCriteria('toughness', { operator: e.target.value })}
                >
                  {COMPARISON_OPS.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  className="field-input small"
                  min="0"
                  placeholder="0"
                  value={criteria.toughness.value}
                  onChange={(e) => updateCriteria('toughness', { value: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Other Section */}
      <div className="search-section">
        <button
          type="button"
          className={`section-header ${expanded.other ? 'expanded' : ''}`}
          onClick={() => toggleSection('other')}
        >
          <span className="section-icon">{expanded.other ? '▼' : '▶'}</span>
          <span className="section-title">Set, Rarity, Artist & Format</span>
        </button>
        
        {expanded.other && (
          <div className="section-content">
            {/* Language */}
            <div className="field-group">
              <label className="field-label">Language</label>
              <select
                className="field-select"
                value={criteria.lang.value}
                onChange={(e) => updateCriteria('lang', { value: e.target.value })}
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Set */}
            <div className="field-group">
              <label className="field-label">Set (code or name)</label>
              <input
                type="text"
                className="field-input"
                placeholder="e.g., MH2 or Modern Horizons 2"
                value={criteria.set.value}
                onChange={(e) => updateCriteria('set', { value: e.target.value })}
              />
            </div>

            {/* Rarity */}
            <div className="field-group">
              <label className="field-label">Rarity</label>
              <div className="rarity-selector">
                {RARITIES.map(rarity => (
                  <button
                    key={rarity}
                    type="button"
                    className={`rarity-btn rarity-${rarity} ${
                      criteria.rarity.values.includes(rarity) ? 'selected' : ''
                    }`}
                    onClick={() => handleRarityToggle(rarity)}
                  >
                    {rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Artist */}
            <div className="field-group">
              <label className="field-label">
                Artist
                <select
                  className="operator-select"
                  value={criteria.artist.operator}
                  onChange={(e) => updateCriteria('artist', { operator: e.target.value })}
                >
                  <option value="OR">Match Any (OR)</option>
                  <option value="AND">Match All (AND)</option>
                </select>
              </label>
              {criteria.artist.values.map((value, index) => (
                <div key={index} className="field-row">
                  <input
                    type="text"
                    className="field-input"
                    placeholder="e.g., Brad Rigney"
                    value={value}
                    onChange={(e) => updateArrayField('artist', index, e.target.value)}
                  />
                  {criteria.artist.values.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeArrayField('artist', index)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="add-btn"
                onClick={() => addArrayField('artist')}
              >
                + Add Artist
              </button>
            </div>

            {/* Format Legality */}
            <div className="field-group">
              <label className="field-label">Format Legality</label>
              <div className="field-row">
                <select
                  className="field-select"
                  value={criteria.legality.format}
                  onChange={(e) => updateCriteria('legality', { format: e.target.value })}
                >
                  <option value="">Any Format</option>
                  {FORMATS.map(format => (
                    <option key={format} value={format}>
                      {format.charAt(0).toUpperCase() + format.slice(1)}
                    </option>
                  ))}
                </select>
                <select
                  className="field-select"
                  value={criteria.legality.status}
                  onChange={(e) => updateCriteria('legality', { status: e.target.value })}
                  disabled={!criteria.legality.format}
                >
                  <option value="legal">Legal</option>
                  <option value="not_legal">Not Legal</option>
                  <option value="restricted">Restricted</option>
                  <option value="banned">Banned</option>
                </select>
              </div>
            </div>

            {/* Keywords */}
            <div className="field-group">
              <label className="field-label">
                Keywords
                <select
                  className="operator-select"
                  value={criteria.keywords.operator}
                  onChange={(e) => updateCriteria('keywords', { operator: e.target.value })}
                >
                  <option value="OR">Match Any (OR)</option>
                  <option value="AND">Match All (AND)</option>
                </select>
              </label>
              {criteria.keywords.values.map((value, index) => (
                <div key={index} className="field-row">
                  <input
                    type="text"
                    className="field-input"
                    placeholder="e.g., Flying, Trample"
                    value={value}
                    onChange={(e) => updateArrayField('keywords', index, e.target.value)}
                  />
                  {criteria.keywords.values.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeArrayField('keywords', index)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="add-btn"
                onClick={() => addArrayField('keywords')}
              >
                + Add Keyword
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="search-actions">
        <button type="button" className="reset-btn" onClick={handleReset}>
          Reset
        </button>
        <button type="submit" className="submit-btn">
          Search Cards
        </button>
      </div>
    </form>
  );
}

export default AdvancedSearch;
